import { Box, FormGroupContext, Link, Text } from '@ksyos/design-system';
import { useCallback, useEffect, useState } from 'react';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import type { IEpisodeCategory } from '@common/interfaces';
import { EpisodeCategoryFilter } from '@frontend/components/pages/outpatientClinic/components/filters/EpisodeCategoryFilter';
import { getSelectedLanguage } from '@frontend/translate';
import type { OutpatientClinicTaskListFilter } from '../types/outpatientClinicTaskListFilter';
import { emptyOutpatientClinicTaskListFilter } from '../constants/emptyOutpatientClinicTaskListFilter';
import { CategoryFilter } from './filters/CategoryFilter';
import { DateRangeFilter } from './filters/DateRangeFilter';
import { PerformerFilter } from './filters/PerformerFilter';
import { SearchTextFilter } from './filters/SearchTextFilter';
import { TypeFilter } from './filters/TypeFilter';

export type OutpatientClinicTaskListFilterPanelProps = {
    episodeCategories: IEpisodeCategory[];
    filter: OutpatientClinicTaskListFilter;
    practitioners: NonNullable<OutpatientClinicTask['performerPractitioner']>[];
    professions: NonNullable<OutpatientClinicTask['performerPractitionerProfession']>[];
    setFilter: (filter: OutpatientClinicTaskListFilter) => void;
    types: OutpatientClinicTask['type'][];
};

export function OutpatientClinicTaskListFilterPanel({
    episodeCategories,
    filter,
    types,
    practitioners,
    professions,
    setFilter,
}: OutpatientClinicTaskListFilterPanelProps) {
    const translate = getSelectedLanguage();

    const [localType, setLocalType] = useState(filter.type);
    const [localPractitioners, setLocalPractitioners] = useState(filter.performerPractitioner);
    const [localProfessions, setLocalProfessions] = useState(filter.performerPractitionerProfession);
    const [localEpisodeCategories, setLocalEpisodeCategories] = useState(filter.episodeCategory);
    const [localSearchText, setLocalSearchText] = useState(filter.searchText ?? '');
    const [localCategory, setLocalCategory] = useState(filter.category);

    const emptyFilters = useCallback(() => {
        setFilter({
            ...emptyOutpatientClinicTaskListFilter,
        });
        setLocalType(undefined);
        setLocalCategory(undefined);
        setLocalProfessions(undefined);
        setLocalPractitioners(undefined);
        setLocalEpisodeCategories(undefined);
        setLocalSearchText('');
    }, [setFilter]);

    useEffect(() => {
        setLocalType(filter.type);
        setLocalCategory(filter.category);
        setLocalProfessions(filter.performerPractitionerProfession);
        setLocalPractitioners(filter.performerPractitioner);
        setLocalEpisodeCategories(filter.episodeCategory);
        setLocalSearchText(filter.searchText ?? '');
    }, [filter]);

    return (
        <Box
            display="grid"
            gridTemplateColumns={[
                '1fr',
                'repeat(auto-fill, minmax(168px, 1fr))',
                null,
                null,
                'var(--searchText) var(--dateRange) var(--episodeCategory) var(--category) var(--type) var(--performer) var(--emptyFilters)',
            ]}
            mb={4}
            sx={{
                '--category': '132px',
                '--dateRange': 'repeat(2, max-content)',
                '--emptyFilters': 'max-content',
                '--episodeCategory': '132px',
                '--performer': '132px',
                '--searchText': 'minmax(min-content, 568px)',
                '--type': '132px',
                columnGap: 'var(--kds-spacing-3)',
            }}
        >
            <FormGroupContext.Provider value={{ layout: 'vertical', size: 'large', variant: 'outline' }}>
                <SearchTextFilter
                    filter={filter}
                    localSearchText={localSearchText}
                    setFilter={setFilter}
                    setLocalSearchText={setLocalSearchText}
                />
                <DateRangeFilter
                    filter={filter}
                    setFilter={setFilter}
                />
                <EpisodeCategoryFilter
                    episodeCategories={episodeCategories}
                    filter={filter}
                    localEpisodeCategories={localEpisodeCategories}
                    setFilter={setFilter}
                    setLocalEpisodeCategories={setLocalEpisodeCategories}
                />
                <CategoryFilter
                    filter={filter}
                    localCategory={localCategory}
                    setFilter={setFilter}
                    setLocalCategory={setLocalCategory}
                    types={types}
                />
                <TypeFilter
                    filter={filter}
                    localType={localType}
                    setFilter={setFilter}
                    setLocalType={setLocalType}
                    types={types}
                />
                <PerformerFilter
                    filter={filter}
                    localPractitioners={localPractitioners}
                    localProfessions={localProfessions}
                    practitioners={practitioners}
                    professions={professions}
                    setFilter={setFilter}
                    setLocalPractitioners={setLocalPractitioners}
                    setLocalProfessions={setLocalProfessions}
                />
                <Text
                    as="span"
                    size="sm"
                    sx={{ alignSelf: 'center' }}
                >
                    <Link
                        color="default"
                        fontWeight="normal"
                        onClick={emptyFilters}
                        variant="button"
                    >
                        {translate.common.deleteAll}
                    </Link>
                </Text>
            </FormGroupContext.Provider>
        </Box>
    );
}
